import { useState } from 'react';

declare function gtag(...args: unknown[]): void;

const HERO_FORM_URL = 'https://readdy.ai/api/form/d74aah75hic0eqh31310';

function HeroEstimateForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    const form = e.currentTarget;
    const data = new FormData(form);
    fetch(HERO_FORM_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
    })
      .then((res) => {
        if (res.ok) {
          setStatus('success');
          form.reset();
          if (typeof gtag === 'function') {
            gtag('event', 'conversion', {
              send_to: 'AW-17076629084/v2T0CLqaiZgcENzc4s4_',
            });
          }
        } else {
          setStatus('error');
        }
      })
      .catch(() => setStatus('error'));
  };

  if (status === 'success') {
    return (
      <div className="bg-green-50 border-2 border-green-500 rounded-xl p-6 text-center">
        <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3">
          <i className="ri-check-double-line text-green-600 text-3xl"></i>
        </div>
        <p className="font-black text-green-800 text-lg">Request Sent!</p>
        <p className="text-green-700 text-sm mt-1">We'll be in touch same day. For immediate help, call <a href="tel:7252675482" className="font-bold underline">(725) 267-5482</a>.</p>
      </div>
    );
  }

  return (
    <form
      id="hero-estimate-form"
      data-readdy-form
      action={HERO_FORM_URL}
      method="POST"
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <div>
        <input type="text" name="name" placeholder="Your Name *" required className="w-full px-4 py-4 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-cyan-500" />
      </div>
      <div>
        <input type="email" name="email" placeholder="Email Address (optional)" className="w-full px-4 py-4 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-cyan-500" />
      </div>
      <div>
        <input type="tel" name="phone" placeholder="Your phone number *" required className="w-full px-4 py-4 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-cyan-500" />
      </div>
      <div>
        <textarea name="message" placeholder="Describe your issue (high water bill, visible leak, water damage...)..." rows={3} maxLength={500} className="w-full px-4 py-4 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"></textarea>
      </div>
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full bg-[#0f1f3d] text-white px-6 py-4 rounded-lg font-bold text-base hover:bg-[#162447] transition-colors whitespace-nowrap cursor-pointer border-2 border-[#0f1f3d] disabled:opacity-60"
      >
        {status === 'submitting' ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i> Sending...
          </span>
        ) : 'Get Free Estimate'}
      </button>
      {status === 'error' && (
        <p className="text-center text-sm text-red-600 font-semibold">Something went wrong. Please call us directly at (725) 267-5482.</p>
      )}
      <p className="text-center text-xs text-gray-500">Same-day response • No obligation • Licensed &amp; Insured</p>
    </form>
  );
}

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/dba6aeeba042513317da173cfed579f8.jpg"
          alt="Same-Day Leak Detection Las Vegas — Call Now for Immediate Service"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0f1f3d]/88 to-[#0a1628]/80"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[110px] lg:pt-20 pb-20 lg:pb-56 w-full">
        {/* Desktop Layout - Two Column */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm sm:text-base font-bold mb-3 animate-pulse">
              ⚡ SAME-DAY SERVICE AVAILABLE — LAS VEGAS
            </div>
            {/* Google Rating Badge */}
            <div className="flex items-center gap-2 mb-5">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(s => (
                    <i key={s} className="ri-star-fill text-yellow-400 text-sm"></i>
                  ))}
                </div>
                <span className="text-white font-bold text-sm">5.0 Rated on Google</span>
                <span className="text-gray-300 text-sm">(23+ Reviews)</span>
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-3 leading-tight">
              Same-Day Leak Detection in Las Vegas
              <span className="block text-cyan-300 text-3xl sm:text-4xl lg:text-5xl mt-2">Find the Leak Fast — No Damage, No Guesswork</span>
            </h1>
            <p className="text-base sm:text-lg text-cyan-300 font-semibold mb-1">
              Call Now for Immediate Service
            </p>
            <p className="text-lg sm:text-xl text-cyan-300 font-bold mb-4 tracking-wide">
              Fast Response &nbsp;•&nbsp; 24/7 Emergency Service &nbsp;•&nbsp; Licensed &amp; Insured
            </p>
            <p className="text-sm sm:text-base text-yellow-200 font-semibold mb-4 italic">
              Speak directly to a licensed leak detection specialist — not a call center.
            </p>
            <p className="text-base sm:text-lg text-gray-200 mb-8 leading-relaxed">
              Call now to stop the damage and get a <strong className="text-white">fast, accurate answer</strong> today. Flat-rate pricing — no surprises.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:7252675482" className="bg-red-600 text-white px-8 py-5 rounded-lg font-black text-lg sm:text-xl hover:bg-red-500 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2">
                <i className="ri-phone-fill text-white text-2xl"></i> Call Now
              </a>
              <a href="#contact" className="bg-white text-[#0f1f3d] px-8 py-5 rounded-lg font-bold text-lg sm:text-xl hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-center">
                Get Free Estimate
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-400 italic">No-obligation inspection • Immediate service available • Same-day repairs</p>

            {/* Hearth Financing Banner */}
            <div className="mt-6">
              <a
                href="https://app.gethearth.com/partners/leak-detectives?utm_campaign=58023&utm_content=darkblue&utm_medium=contractor-website&utm_source=contractor&utm_term=700x110"
                target="_blank"
                rel="nofollow"
              >
                <img
                  src="https://app.gethearth.com/contractor_images/leak-detectives/banner.jpg?color=darkblue&size_id=700x110"
                  alt="Hearth Financing"
                  style={{ height: '80px', width: '508px' }}
                  className="rounded-lg"
                />
              </a>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
            <div className="bg-red-50 border-2 border-red-500 rounded-xl px-4 py-3 mb-4 text-center">
              <span className="text-red-700 font-black text-base sm:text-lg">🚨 FREE Estimate — No Commitment Required</span>
              <p className="text-red-600 text-sm mt-1 font-medium">Speak directly to a licensed specialist — not a call center</p>
            </div>

            {/* Call Now — Primary Action */}
            <a
              href="tel:7252675482"
              className="flex items-center justify-center gap-3 w-full bg-red-600 text-white px-6 py-5 rounded-lg font-black text-xl hover:bg-red-500 transition-colors whitespace-nowrap cursor-pointer mb-4"
            >
              <i className="ri-phone-fill text-2xl"></i>
              Call Now — (725) 267-5482
            </a>
            <p className="text-center text-xs text-gray-500 mb-5 italic">Most leaks are found quickly — call now for immediate service.</p>

            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">or request a callback</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            <h3 className="text-xl sm:text-2xl font-bold text-[#0f1f3d] mb-4">Get Your Free Estimate</h3>
            <HeroEstimateForm />
          </div>
        </div>

        {/* Mobile Layout - Call-Focused Vertical Stack */}
        <div className="lg:hidden text-white text-center">
          <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-3 animate-pulse">
            ⚡ SAME-DAY SERVICE AVAILABLE
          </div>
          {/* Google Rating Badge - Mobile */}
          <div className="flex justify-center mb-4">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <i key={s} className="ri-star-fill text-yellow-400 text-sm"></i>
                ))}
              </div>
              <span className="text-white font-bold text-sm">5.0 on Google</span>
              <span className="text-gray-300 text-sm">(23+ Reviews)</span>
            </div>
          </div>
          <h1 className="text-3xl font-black mb-4 leading-tight px-2">
            Same-Day Leak Detection in Las Vegas
            <span className="block text-cyan-300 text-2xl mt-2">Find the Leak Fast — No Damage, No Guesswork</span>
          </h1>
          <p className="text-base text-cyan-300 font-bold mb-5 tracking-wide">
            Call Now for Immediate Service
          </p>

          {/* Primary CTA - Call Now */}
          <div className="space-y-3 mb-6">
            <a
              href="tel:7252675482"
              className="block w-full bg-red-600 text-white px-8 py-6 rounded-lg font-black text-2xl hover:bg-red-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2"></i>
              Call Now
            </a>
            <a
              href="tel:7252675482"
              className="block w-full text-white text-xl font-bold"
            >
              (725) 267-5482
            </a>
            <p className="text-sm text-gray-400 italic">Same-day repairs • No obligation • Immediate service</p>
          </div>

          {/* Secondary CTA */}
          <a
            href="#contact"
            className="block w-full bg-transparent border-2 border-white text-white px-8 py-5 rounded-lg font-bold text-lg hover:bg-white hover:text-[#0f1f3d] transition-all whitespace-nowrap cursor-pointer mb-8"
          >
            Get Free Estimate
          </a>

          {/* Hearth Financing Banner - Mobile */}
          <div className="flex justify-center mb-8">
            <a
              href="https://app.gethearth.com/partners/leak-detectives?utm_campaign=58023&utm_content=darkblue&utm_medium=contractor-website&utm_source=contractor&utm_term=700x110"
              target="_blank"
              rel="nofollow"
            >
              <img
                src="https://app.gethearth.com/contractor_images/leak-detectives/banner.jpg?color=darkblue&size_id=700x110"
                alt="Hearth Financing"
                style={{ height: '80px', width: '508px' }}
                className="rounded-lg mx-auto max-w-full"
              />
            </a>
          </div>

          {/* Trust Badges - Horizontal Scroll */}
          <div className="overflow-x-auto pb-4 -mx-4 px-4">
            <div className="flex gap-4 min-w-max">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 min-w-[200px]">
                <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-lg flex-shrink-0">
                  <i className="ri-customer-service-2-fill text-xl text-white"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm font-bold text-white whitespace-nowrap">24/7 EMERGENCY</div>
                  <div className="text-xs text-blue-200 whitespace-nowrap">Immediate Response</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 min-w-[200px]">
                <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0">
                  <i className="ri-shield-check-fill text-xl text-white"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm font-bold text-white whitespace-nowrap">CERTIFIED SPECIALISTS</div>
                  <div className="text-xs text-blue-200 whitespace-nowrap">Licensed &amp; Insured</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 min-w-[200px]">
                <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0">
                  <i className="ri-calendar-check-fill text-xl text-white"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm font-bold text-white whitespace-nowrap">SAME-DAY REPAIRS</div>
                  <div className="text-xs text-blue-200 whitespace-nowrap">Don&apos;t Wait, Act Now</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Trust Badges Bar */}
      <div className="hidden lg:block absolute bottom-0 left-0 right-0 bg-gradient-to-r from-[#0a1628] via-[#162447] to-[#0a1628] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="w-14 h-14 flex items-center justify-center bg-red-600 rounded-lg flex-shrink-0">
                <i className="ri-customer-service-2-fill text-2xl text-white"></i>
              </div>
              <div>
                <div className="text-base font-bold text-white">24/7 EMERGENCY SERVICE</div>
                <div className="text-sm text-blue-200">Immediate Response Available</div>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="w-14 h-14 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0">
                <i className="ri-shield-check-fill text-2xl text-white"></i>
              </div>
              <div>
                <div className="text-base font-bold text-white">CERTIFIED SPECIALISTS</div>
                <div className="text-sm text-blue-200">Licensed &amp; Insured</div>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="w-14 h-14 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0">
                <i className="ri-calendar-check-fill text-2xl text-white"></i>
              </div>
              <div>
                <div className="text-base font-bold text-white">SAME-DAY REPAIRS</div>
                <div className="text-sm text-blue-200">Free No-Obligation Estimate</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
